import {ReactElement} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'

import {PammOfferType} from '../../../../../../../graphql/types'
import messages from '../../../../../../../assets/messages'
import {getFormattedAmount} from '../../../../../../../common/utils'
import {
  mapIntervalNameToI18nKeyPlural,
  mapIntervalNameToI18nKeySingular,
} from '../../../../../PammManagerSettingsView/components/PammManagerOffers/components/OfferTile'

type OfferDetailsProps = {
  offer: PammOfferType
  classes: Record<string, string>
} & WithNamespaces

/** Joins an offer's performance-fee tiers into a single compact value (e.g. "20%" or "20% / 15%"). */
const formatPerformanceFees = (offer: PammOfferType): string =>
  (offer.settings?.performanceFees?.levels ?? [])
    .map((fee) => `${fee?.value}%`)
    .join(' / ')

/**
 * The connected offer's trading interval as "<count> <unit>", picking the singular or plural
 * unit label so "1 Month" / "3 Months" read naturally.
 */
const TradingIntervalValue = ({offer}: {offer: PammOfferType}): ReactElement => {
  const count = offer.settings?.tradingInterval?.count
  const type = offer.settings?.tradingInterval?.type ?? 'CalendarMonths'
  const unitMap = count === 1 ? mapIntervalNameToI18nKeySingular : mapIntervalNameToI18nKeyPlural

  return (
    <>
      {count} <Trans {...unitMap[type]} />
    </>
  )
}

/**
 * Always-visible summary of the PAMM offer an investor account is connected to, rendered inline
 * on the investor tile (PT-5567). The modern TauroMarkets investor accounts open the external PAMM
 * portal on click, so the terms cannot live behind an internal detail page — they belong on the card
 * itself. Shows the minimum deposit/withdrawal/initial-investment, performance fee, and trading
 * interval, formatted with the same helpers the offer-acceptance page uses so the figures match.
 *
 * @param offer - The PAMM offer connected to the investor account (resolved via `account.offer`).
 * @param classes - Card styles injected by the parent card's `withStyles`.
 */
export const OfferDetails = withNamespaces()(({offer, classes, lng}: OfferDetailsProps): ReactElement => {
  const {currency} = offer
  // A connected offer always carries a currency, but guard the format call so a missing one degrades
  // to a dash instead of throwing inside Intl.NumberFormat and taking the whole card down with it.
  // The amount follows the dashboard UI language (`lng`), not a fixed en-US (PT-7336).
  const amount = (value?: number | null) =>
    currency ? getFormattedAmount({amount: value ?? 0, currency, locale: lng}) : '—'

  return (
    <div className={classes.offerDetails} data-testid="pamm-investor-offer-details">
      <div className={classes.offerDetailsTitle}>
        <Trans {...messages.offerDetails} />
      </div>
      <div className={classes.offerDetailsRow}>
        <span className={classes.offerDetailsLabel}>
          <Trans {...messages.minDeposit} />
        </span>
        <span className={classes.offerDetailsValue}>{amount(offer.settings?.minDeposit)}</span>
      </div>
      <div className={classes.offerDetailsRow}>
        <span className={classes.offerDetailsLabel}>
          <Trans {...messages.minWithdrawal} />
        </span>
        <span className={classes.offerDetailsValue}>{amount(offer.settings?.minWithdrawal)}</span>
      </div>
      <div className={classes.offerDetailsRow}>
        <span className={classes.offerDetailsLabel}>
          <Trans {...messages.minInitialInvestment} />
        </span>
        <span className={classes.offerDetailsValue}>{amount(offer.settings?.minInitialInvestment)}</span>
      </div>
      <div className={classes.offerDetailsRow}>
        <span className={classes.offerDetailsLabel}>
          <Trans {...messages.performanceFees} />
        </span>
        <span className={classes.offerDetailsValue}>{formatPerformanceFees(offer)}</span>
      </div>
      <div className={classes.offerDetailsRow}>
        <span className={classes.offerDetailsLabel}>
          <Trans {...messages.tradingInterval} />
        </span>
        <span className={classes.offerDetailsValue}>
          <TradingIntervalValue offer={offer} />
        </span>
      </div>
    </div>
  )
})
